import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Clock } from "lucide-react";
import { CLINIC_STAGES, VENDOR_STAGES, STAGE_LABEL, fmtDate } from "./constants";
import { ModalShell, Field } from "../_dialog";

export function ClinicPipelineView() {
  return (
    <PipelineKanban
      title="Clinics"
      stages={CLINIC_STAGES}
      endpoint="/founder/clinics"
      itemsKey="clinics"
      RecordDialog={ClinicDialog}
      renderRow={(c, handlers) => <ClinicCard key={c.id} clinic={c} {...handlers} />}
      testId="founder-os-clinic-pipeline"
    />
  );
}

export function VendorPipelineView() {
  return (
    <PipelineKanban
      title="Vendors"
      stages={VENDOR_STAGES}
      endpoint="/founder/vendors"
      itemsKey="vendors"
      RecordDialog={VendorDialog}
      renderRow={(v, handlers) => <VendorCard key={v.id} vendor={v} {...handlers} />}
      testId="founder-os-vendor-pipeline"
    />
  );
}

function PipelineKanban({ title, stages, endpoint, itemsKey, RecordDialog, renderRow, testId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint);
      setItems(r.data?.[itemsKey] || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const grouped = useMemo(() => {
    const map = Object.fromEntries(stages.map((s) => [s, []]));
    for (const it of items) {
      const k = it.stage;
      if (map[k]) map[k].push(it);
    }
    return map;
  }, [items, stages]);

  const handleDrop = async (item, newStage) => {
    if (item.stage === newStage) return;
    try {
      await api.patch(`${endpoint}/${item.id}`, { stage: newStage });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${title.toLowerCase().slice(0, -1)}?`)) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {items.length} {title.toLowerCase()} · drag cards between stages.
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid={`${testId}-create-btn`}
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800"
        >
          <Plus className="w-3.5 h-3.5" /> Add {title.toLowerCase().slice(0, -1)}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
        {stages.map((s) => (
          <PipelineColumn
            key={s}
            stage={s}
            items={grouped[s] || []}
            onDrop={handleDrop}
            renderRow={(it) => renderRow(it, { onEdit: setEditing, onDelete: remove })}
            testId={`${testId}-col-${s}`}
          />
        ))}
      </div>

      {creating && (
        <RecordDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
          endpoint={endpoint}
        />
      )}
      {editing && (
        <RecordDialog
          record={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
          endpoint={endpoint}
        />
      )}
    </div>
  );
}

function PipelineColumn({ stage, items, onDrop, renderRow, testId }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const raw = e.dataTransfer.getData("text/plain");
        if (!raw) return;
        try {
          const item = JSON.parse(raw);
          onDrop(item, stage);
        } catch {
          /* ignore */
        }
      }}
      className={`rounded-xl border bg-white p-2 min-h-[280px] transition-colors ${
        over ? "border-slate-900 bg-slate-50" : "border-slate-200"
      }`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700 truncate">
          {STAGE_LABEL(stage)}
        </div>
        <span className="text-[10px] text-slate-500 bg-slate-100 rounded-full px-1.5 font-semibold">
          {items.length}
        </span>
      </div>
      <div className="space-y-1.5">
        {items.map((it) => (
          <div
            key={it.id}
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify(it))}
            className="cursor-grab active:cursor-grabbing"
          >
            {renderRow(it)}
          </div>
        ))}
      </div>
    </div>
  );
}

function ClinicCard({ clinic, onEdit, onDelete }) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-2 hover:border-slate-400 hover:shadow-sm transition-all"
      data-testid={`clinic-card-${clinic.id}`}
    >
      <div className="text-xs font-bold text-slate-900 truncate">{clinic.clinic_name}</div>
      {clinic.specialty && (
        <div className="text-[10px] text-slate-500 truncate">{clinic.specialty}</div>
      )}
      {clinic.contact_person && (
        <div className="text-[10px] text-slate-500 truncate">{clinic.contact_person}</div>
      )}
      {clinic.next_followup_date && (
        <div className="text-[10px] text-blue-700 inline-flex items-center gap-0.5 mt-1">
          <Clock className="w-2.5 h-2.5" /> {fmtDate(clinic.next_followup_date)}
        </div>
      )}
      <div className="flex items-center justify-end gap-1 mt-1">
        <button
          type="button"
          onClick={() => onEdit(clinic)}
          data-testid={`clinic-edit-${clinic.id}`}
          className="text-[10px] text-slate-500 hover:text-slate-900"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => onDelete(clinic)}
          data-testid={`clinic-delete-${clinic.id}`}
          className="text-rose-500 hover:bg-rose-50 rounded p-0.5"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function VendorCard({ vendor, onEdit, onDelete }) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-2 hover:border-slate-400 hover:shadow-sm transition-all"
      data-testid={`vendor-card-${vendor.id}`}
    >
      <div className="text-xs font-bold text-slate-900 truncate">{vendor.vendor_name}</div>
      {vendor.category && (
        <div className="text-[10px] text-slate-500 truncate">{vendor.category}</div>
      )}
      {vendor.recurring_capable && (
        <div className="text-[9px] uppercase tracking-wider rounded-full bg-emerald-50 text-emerald-700 px-1.5 py-0.5 font-semibold inline-block mt-1">
          Recurring
        </div>
      )}
      <div className="flex items-center justify-end gap-1 mt-1">
        <button
          type="button"
          onClick={() => onEdit(vendor)}
          data-testid={`vendor-edit-${vendor.id}`}
          className="text-[10px] text-slate-500 hover:text-slate-900"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => onDelete(vendor)}
          data-testid={`vendor-delete-${vendor.id}`}
          className="text-rose-500 hover:bg-rose-50 rounded p-0.5"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function ClinicDialog({ record, onClose, onSaved, endpoint }) {
  const [form, setForm] = useState({
    clinic_name: record?.clinic_name || "",
    specialty: record?.specialty || "",
    contact_person: record?.contact_person || "",
    contact_email: record?.contact_email || "",
    notes: record?.notes || "",
    pain_points: record?.pain_points || "",
    next_followup_date: record?.next_followup_date ? record.next_followup_date.slice(0, 10) : "",
    stage: record?.stage || "prospect",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.clinic_name.trim()) {
      toast.error("Clinic name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, next_followup_date: form.next_followup_date || null };
      if (record) {
        await api.patch(`${endpoint}/${record.id}`, payload);
        toast.success("Clinic updated.");
      } else {
        await api.post(endpoint, payload);
        toast.success("Clinic added.");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={record ? "Edit clinic" : "Add clinic"}
      eyebrow="Clinic Pipeline"
      onClose={onClose}
      testId="clinic-dialog"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
            data-testid="clinic-dialog-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="clinic-dialog-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <Field label="Clinic name">
        <input
          type="text"
          value={form.clinic_name}
          onChange={(e) => set("clinic_name", e.target.value)}
          autoFocus
          data-testid="clinic-dialog-name"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Specialty">
          <input
            type="text"
            value={form.specialty}
            onChange={(e) => set("specialty", e.target.value)}
            data-testid="clinic-dialog-specialty"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Stage">
          <select
            value={form.stage}
            onChange={(e) => set("stage", e.target.value)}
            data-testid="clinic-dialog-stage"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            {CLINIC_STAGES.map((s) => (
              <option key={s} value={s}>{STAGE_LABEL(s)}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Contact person">
          <input
            type="text"
            value={form.contact_person}
            onChange={(e) => set("contact_person", e.target.value)}
            data-testid="clinic-dialog-contact"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Contact email">
          <input
            type="email"
            value={form.contact_email}
            onChange={(e) => set("contact_email", e.target.value)}
            data-testid="clinic-dialog-email"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </Field>
      </div>
      <Field label="Next follow-up">
        <input
          type="date"
          value={form.next_followup_date}
          onChange={(e) => set("next_followup_date", e.target.value)}
          data-testid="clinic-dialog-followup"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <Field label="Operational pain points">
        <textarea
          rows={2}
          value={form.pain_points}
          onChange={(e) => set("pain_points", e.target.value)}
          data-testid="clinic-dialog-pain"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <Field label="Notes">
        <textarea
          rows={3}
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          data-testid="clinic-dialog-notes"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
    </ModalShell>
  );
}

function VendorDialog({ record, onClose, onSaved, endpoint }) {
  const [form, setForm] = useState({
    vendor_name: record?.vendor_name || "",
    category: record?.category || "",
    contact_email: record?.contact_email || "",
    contact_phone: record?.contact_phone || "",
    recurring_capable: !!record?.recurring_capable,
    notes: record?.notes || "",
    stage: record?.stage || "prospect",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.vendor_name.trim()) {
      toast.error("Vendor name is required.");
      return;
    }
    setSaving(true);
    try {
      if (record) {
        await api.patch(`${endpoint}/${record.id}`, form);
        toast.success("Vendor updated.");
      } else {
        await api.post(endpoint, form);
        toast.success("Vendor added.");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={record ? "Edit vendor" : "Add vendor"}
      eyebrow="Vendor Pipeline"
      onClose={onClose}
      testId="vendor-dialog"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
            data-testid="vendor-dialog-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="vendor-dialog-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <Field label="Vendor name">
        <input
          type="text"
          value={form.vendor_name}
          onChange={(e) => set("vendor_name", e.target.value)}
          autoFocus
          data-testid="vendor-dialog-name"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <input
            type="text"
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            placeholder="diagnostics / wellness / …"
            data-testid="vendor-dialog-category"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Stage">
          <select
            value={form.stage}
            onChange={(e) => set("stage", e.target.value)}
            data-testid="vendor-dialog-stage"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            {VENDOR_STAGES.map((s) => (
              <option key={s} value={s}>{STAGE_LABEL(s)}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Contact email">
          <input
            type="email"
            value={form.contact_email}
            onChange={(e) => set("contact_email", e.target.value)}
            data-testid="vendor-dialog-email"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Contact phone">
          <input
            type="tel"
            value={form.contact_phone}
            onChange={(e) => set("contact_phone", e.target.value)}
            data-testid="vendor-dialog-phone"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
        <input
          type="checkbox"
          checked={form.recurring_capable}
          onChange={(e) => set("recurring_capable", e.target.checked)}
          data-testid="vendor-dialog-recurring"
        />
        Recurring-capable vendor
      </label>
      <Field label="Notes">
        <textarea
          rows={3}
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          data-testid="vendor-dialog-notes"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
    </ModalShell>
  );
}

// ===========================================================================
// Feedback
